import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import GhostButton from '../oracle/GhostButton';
import AurumButton from '../oracle/AurumButton';
import { COPY } from '../../contracts/copyMap';
import {
  SUPPORT_TOPIC_KEYS,
  SUPPORT_MESSAGE_MAX,
  validateSupportInput,
} from '../../contracts/supportApi';

// SupportModal — the in-app contact form at the foot of /account.
//
//   [CONTACT SUPPORT] ──▶ form ──▶ sendSupportMessage ──▶ sent panel
//                          ▲                                  │
//                          └────── error, form preserved ◀────┘
//
// ── THE MESSAGE SURVIVES A FAILURE ──────────────────────────────────────────
//
// On error the form stays exactly as typed. Somebody writing to support has
// already had one thing go wrong; losing the paragraph they just wrote to a
// failed send is the second, and it is the one that makes them give up. The
// only state that clears is on a successful send, and on re-open.
//
// ── VISUAL REGISTER ─────────────────────────────────────────────────────────
//
// Quiet, like CancelMembershipModal and unlike UnlockGateModal. This is not a
// selling surface. Bone type on Ash, one Aurum accent on the selected topic and
// the send button. No red — DESIGN.md forbids it even for errors.

export default function SupportModal({
  open,
  onSend,
  onDismiss,
  submitting = false,
  error = null,
  sent = false,
  supportAddress = COPY.CONTACT_EMAIL,
}) {
  const [topic, setTopic] = useState(null);
  const [message, setMessage] = useState('');
  const textareaRef = useRef(null);

  // Reset only when the modal opens, never on error — see the note above.
  useEffect(() => {
    if (open) {
      setTopic(null);
      setMessage('');
    }
  }, [open]);

  useEffect(() => {
    if (!open || sent) return undefined;
    // Focus the message once the entry animation has settled, so the caret does
    // not ride in on a moving element.
    const t = setTimeout(() => textareaRef.current?.focus(), 340);
    return () => clearTimeout(t);
  }, [open, sent]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !submitting) onDismiss?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onDismiss, submitting]);

  // The same validator the server runs. Here it only drives the disabled state;
  // the server copy is the guarantee.
  const valid = validateSupportInput({ topic, message }).ok;
  const remaining = SUPPORT_MESSAGE_MAX - message.trim().length;

  const submit = () => {
    if (!valid || submitting) return;
    onSend?.({ topic, message: message.trim() });
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-obsidian/[0.88] p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.24, ease: 'easeOut' }}
          role="dialog"
          aria-modal="true"
          aria-label={COPY.SUPPORT_MODAL_HEADLINE}
          data-testid="support-modal"
          onClick={submitting ? undefined : onDismiss}
        >
          <motion.div
            className="relative w-[520px] max-w-full max-h-[92vh] overflow-y-auto scrollbar-hide rounded-modal border border-hairline bg-obsidian-raised p-7"
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.32, ease: [0.34, 1.16, 0.64, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="m-0 font-display uppercase tracking-[0.24em] text-bone-dim" style={{ fontSize: 10 }}>
              {COPY.SUPPORT_MODAL_EYEBROW}
            </p>

            {sent ? (
              <div data-testid="support-sent">
                <h2 className="m-0 mt-2 font-display text-[26px] leading-tight text-bone">
                  {COPY.SUPPORT_SENT_HEADLINE}
                </h2>
                <p className="m-0 mt-2 font-display text-[14.5px] text-bone-dim">
                  {COPY.SUPPORT_SENT_BODY}
                </p>
                <div className="mt-7 flex justify-end">
                  <AurumButton onClick={onDismiss} data-testid="support-close">
                    {COPY.SUPPORT_SENT_DISMISS}
                  </AurumButton>
                </div>
              </div>
            ) : (
              <>
                <h2 className="m-0 mt-2 font-display text-[26px] leading-tight text-bone">
                  {COPY.SUPPORT_MODAL_HEADLINE}
                </h2>
                <p className="m-0 mt-2 font-display text-[14.5px] text-bone-dim">
                  {COPY.SUPPORT_MODAL_BODY}
                </p>

                <div className="mt-6" role="radiogroup" aria-label={COPY.SUPPORT_TOPIC_LABEL}>
                  <p className="m-0 mb-3 font-display uppercase tracking-[0.2em] text-bone-dim" style={{ fontSize: 11 }}>
                    {COPY.SUPPORT_TOPIC_LABEL}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {SUPPORT_TOPIC_KEYS.map((key) => {
                      const selected = topic === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          data-testid={`support-topic-${key}`}
                          onClick={() => setTopic(selected ? null : key)}
                          className={[
                            'rounded-thumb border px-4 py-2 font-display text-[14px]',
                            'transition-colors duration-150 cursor-pointer',
                            selected
                              ? 'border-aurum text-aurum bg-transparent'
                              : 'border-hairline text-bone-dim bg-transparent hover:text-bone',
                          ].join(' ')}
                        >
                          {COPY.SUPPORT_TOPIC_LABELS[key]}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-6">
                  <label
                    htmlFor="support-message"
                    className="block m-0 mb-3 font-display uppercase tracking-[0.2em] text-bone-dim"
                    style={{ fontSize: 11 }}
                  >
                    {COPY.SUPPORT_MESSAGE_LABEL}
                  </label>
                  <textarea
                    id="support-message"
                    ref={textareaRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength={SUPPORT_MESSAGE_MAX}
                    rows={6}
                    placeholder={COPY.SUPPORT_MESSAGE_PLACEHOLDER}
                    data-testid="support-message"
                    className="w-full resize-none rounded-field border border-hairline bg-[#1B1B1A] px-4 py-3 font-display text-[15px] text-bone placeholder:text-bone-dim placeholder:italic focus:outline-none focus:border-[rgba(255,255,255,0.4)]"
                  />
                  {/* Only counts down near the ceiling. A counter sitting at
                      "1,974 left" from the first keystroke reads as a demand
                      for length rather than a limit. */}
                  {remaining <= 200 && (
                    <p
                      className="m-0 mt-2 text-right font-display text-[12px] text-bone-dim"
                      data-testid="support-remaining"
                    >
                      {remaining}
                    </p>
                  )}
                </div>

                {error && (
                  <p className="m-0 mt-4 font-display text-[13.5px] text-bone" role="alert" data-testid="support-error">
                    {error}
                  </p>
                )}

                <div className="mt-7 flex items-center justify-between gap-3">
                  <p className="m-0 font-display text-[12.5px] text-bone-dim">
                    {COPY.SUPPORT_DIRECT_NOTE}{' '}
                    <a href={`mailto:${supportAddress}`} className="text-bone-dim underline">
                      {supportAddress}
                    </a>
                  </p>
                  <div className="flex items-center gap-3">
                    <GhostButton onClick={onDismiss} disabled={submitting} data-testid="support-cancel">
                      {COPY.SUPPORT_CANCEL}
                    </GhostButton>
                    <AurumButton
                      onClick={submit}
                      disabled={!valid || submitting}
                      loading={submitting}
                      data-testid="support-send"
                    >
                      {COPY.SUPPORT_SEND}
                    </AurumButton>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
